import { action, batch, withAssign } from '@reatom/framework'
import { BigNumber } from 'bignumber.js'
import i18next from 'i18next'

import { Result } from '@blade/std-lib'
import { blockHashAtom } from '@root/entities/blockhash/atoms'
import { Network } from '@root/entities/network'
import { Pair, type PairAddress } from '@root/entities/pair/model'
import { existingInviteCodeAtom } from '@root/entities/referral'
import {
  TradeSettings,
  TradeSettingsSolRpc,
} from '@root/entities/trade-settings'
import { TxHash } from '@root/entities/transaction'
import { reatomFeeConfigAtom } from '@root/features/services/fee-config/atoms'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error, UnknownError } from '@root/shared'
import { ApiError } from '@root/shared/api'
import { SwapErrorCode } from '@root/shared/api/api-error-code'
import { reatomRequest } from '@root/shared/lib/reatom'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { solRpcSettingsModelAtom } from '../../trade-settings/atoms/reatom-trade-settings-sol-rpc'

import { prepareAndSignPermitAction } from './reatom-permit'
import {
  sendMultipleSwapTransactionsRequestAction,
  sendSwapTransactionRequestAction,
  type ProcessSwapArgs,
} from './swap-requests'

import type {
  ProcessBulkSwapArgs,
  ProcessBulkSwapEntryArgs,
  ProcessSwapCommonArgs,
  SendSwapData,
} from './swap-requests'
import type {
  TradePreparePermitInstructionsCreateData,
  TradeSendMultipleSwapTransactionsCreatePayload,
} from '@blade/trade-api-client'
import type { CurrencyModel } from '@root/entities/currency'
import type { BuildSignAndSendSwapTransactionPayloadDto } from '@root/features/secure-storage/secure-storage-service'
import type { FeeConfig } from '@root/features/services/fee-config/model'

const prepareCommonSwapArgsAction = action(
  (ctx, req: ProcessSwapCommonArgs & { feeConfig: FeeConfig | undefined }) => {
    const feeConfig = req.feeConfig
      ? req.feeConfig
      : ctx.get(reatomFeeConfigAtom).value

    const blockhash = ctx.get(blockHashAtom)?.blockhash
    const accountsDto = req.accounts?.[req.type]
    const accounts = accountsDto
      ? Pair.poolAccountsToSecureStoragePoolAccounts(req.provider, accountsDto)
      : undefined

    if (!feeConfig || !blockhash) {
      // eslint-disable-next-line no-console
      console.error(
        `Missing ${[
          ['feeConfig', feeConfig],
          ['blockhash', blockhash],
        ]
          .filter(([_, value]) => value === undefined)
          .map(([key]) => key)}`,
      )
      return Result.fail(UnknownError.fromUnknown('Invalid input'))
    }

    const rpcSettingsModel = ctx.get(solRpcSettingsModelAtom)
    if (!rpcSettingsModel) {
      return Result.fail(UnknownError.fromUnknown('No rpc settings model'))
    }
    const rpc = ctx.get(rpcSettingsModel.selectedRpc)

    const inviteCode = ctx.get(existingInviteCodeAtom)

    return Result.succeed({
      feeConfig,
      blockhash,
      accounts,
      inviteCode,
      rpc,
    })
  },
  'prepareCommonSwapArgsAction',
)

const processSolanaSwapAction = action(async (ctx, req: ProcessSwapArgs) => {
  const {
    secureStorageService,
    environment: { pathfinderEnabled },
  } = ctx.get(dependenciesAtom)

  const commonArgsResult = prepareCommonSwapArgsAction(ctx, {
    ...req,
    feeConfig: req.quote.feeConfig,
  })

  if (Result.isFailure(commonArgsResult)) {
    return Result.fail(commonArgsResult.failure)
  }

  const { feeConfig, blockhash, accounts, inviteCode, rpc } =
    commonArgsResult.success

  const swapSettings = req.swapSettings
  if (swapSettings.network !== Network.Solana) {
    return Result.fail(UnknownError.fromUnknown('Invalid network'))
  }

  const rawBn = req.rawAmountIn ? BigNumber(req.rawAmountIn) : undefined
  const amountIn =
    rawBn && rawBn.isFinite() && rawBn.gt(0)
      ? rawBn
      : BigNumber(req.quote.inputAmountRaw).integerValue(BigNumber.ROUND_FLOOR)
  const amountOut = BigNumber(req.quote.outputAmountRaw)
    .multipliedBy(1 - req.swapSettings.slippage / 100)
    .integerValue(BigNumber.ROUND_FLOOR)

  const sender = TradeSettingsSolRpc.toPathfinderArg(rpc)

  const storageResult = await ctx.schedule(() => {
    if (pathfinderEnabled) {
      const txToSign = req.quote.txToSign
      if (!txToSign) {
        return Result.fail(UnknownError.fromUnknown('No tx to sign'))
      }

      return secureStorageService.singAndSendPathfinderSwapTransaction({
        amountIn: amountIn.toString(),
        operation: req.type,
        txBase64: txToSign,
        walletId: req.walletId,
        sender: sender,
      })
    }

    if (!accounts) {
      return Result.fail(UnknownError.fromUnknown('No accounts'))
    }

    return secureStorageService.buildSignAndSendSwapTransaction({
      walletId: req.walletId,
      operation: req.type,
      provider: req.provider.id,
      referralCode: inviteCode,
      mint: req.pairQuoteCurrency,
      tokenProgram: req.pairQuoteTokenProgram,
      amountIn: amountIn.toString(),
      amountOut: amountOut.toString(),
      signer: req.walletAddress,
      poolAccounts: accounts,
      feeConfig,
      blockhash,
      poolId: req.pairAddress,
      sender,
      jitoEnabled: swapSettings.jitoEnabled,
      jitoTipSol: swapSettings.jitoTip,
      priorityFeeSol: swapSettings.priorityFee,
    })
  })
  const result = Result.unify(storageResult)

  if (Result.isSuccess(result)) {
    sendSwapTransactionRequestAction(ctx, {
      ...req,
      txSignature: result.success.signature,
    })
    swapDoneEvent(ctx, {
      pairAddress: req.pairAddress,
      txHash: TxHash(result.success.signature),
    })
  }

  return result.pipe(
    Result.mapError(UnknownError.fromUnknown),
    Result.map(
      (x): SendSwapData => ({
        tx: TxHash(x.signature),
        status: 'finished',
        walletId: req.walletId,
      }),
    ),
  )
}, 'processSolanaSwapAction')

const processSolanaBulkSwapAction = action(
  async (ctx, req: ProcessBulkSwapArgs) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)

    const commonArgsResult = prepareCommonSwapArgsAction(ctx, {
      ...req,
      feeConfig: undefined,
    })

    if (Result.isFailure(commonArgsResult)) {
      return Result.fail(commonArgsResult.failure)
    }

    const { feeConfig, blockhash, accounts, inviteCode, rpc } =
      commonArgsResult.success

    const swapSettings = req.swapSettings
    if (swapSettings.network !== Network.Solana) {
      return Result.fail(UnknownError.fromUnknown('Invalid network'))
    }

    if (!accounts) {
      return Result.fail(UnknownError.fromUnknown('No accounts'))
    }

    const sender = TradeSettingsSolRpc.toPathfinderArg(rpc)

    const swaps = req.swaps.map((it): ProcessBulkSwapEntryArgs => {
      const amountIn = BigNumber.isBigNumber(it.amountIn)
        ? BigNumber(it.amountIn).integerValue(BigNumber.ROUND_FLOOR)
        : it.amountIn

      return {
        walletId: it.walletId,
        walletAddress: it.walletAddress,
        amountIn: amountIn,
        amountOut: BigNumber(0),
        rawAmountIn: it.rawAmountIn,
      }
    })

    const signInputs = swaps.map(
      (it): BuildSignAndSendSwapTransactionPayloadDto => {
        return {
          walletId: it.walletId,
          amountIn:
            it.rawAmountIn &&
            BigNumber(it.rawAmountIn).isFinite() &&
            BigNumber(it.rawAmountIn).gt(0)
              ? it.rawAmountIn
              : it.amountIn.toString(),
          amountOut: it.amountOut.toString(),
          operation: req.type,
          provider: req.provider.id,
          referralCode: inviteCode,
          mint: req.pairQuoteCurrency,
          tokenProgram: req.pairQuoteTokenProgram,
          signer: it.walletAddress,
          poolAccounts: accounts,
          feeConfig,
          blockhash,
          poolId: req.pairAddress,
          sender,
          jitoEnabled: swapSettings.jitoEnabled,
          jitoTipSol: swapSettings.jitoTip,
          priorityFeeSol: swapSettings.priorityFee,
        }
      },
    )

    const result = await ctx.schedule(() => {
      return secureStorageService.bulkBuildSignAndSendSwapTransaction(
        signInputs,
      )
    })

    if (Result.isSuccess(result)) {
      batch(ctx, () => {
        for (const it of result.success) {
          if (it.error === undefined) {
            swapDoneEvent(ctx, {
              pairAddress: req.pairAddress,
              txHash: TxHash(it.signature),
            })
          }
        }
      })
      const sendReqWallets = result.success
        .map((it) =>
          it.signature
            ? {
                walletId: it.walletId,
                signedTx: it.signature,
              }
            : undefined,
        )
        .filter((it) => it !== undefined)

      if (sendReqWallets.length > 0) {
        const sendReq: TradeSendMultipleSwapTransactionsCreatePayload = {
          network: Network.toDto(req.network),
          amount: '0',
          amountOut: 0,
          tokenAddress: req.pairQuoteCurrency,
          path: [req.inAddress, req.outAddress],
          provider: req.provider.id,
          pairAddress: req.pairAddress,
          swapSettings: TradeSettings.toDto(req.swapSettings),
          type: req.type,
          wallets: sendReqWallets,
        }
        sendMultipleSwapTransactionsRequestAction(ctx, sendReq)
      }
    }

    return result.pipe(Result.mapError(UnknownError.fromUnknown))
  },
  'processSolanaBulkSwapAction',
)

export const processBulkSwapAction = action(
  async (ctx, req: ProcessBulkSwapArgs) => {
    if (req.network === Network.Solana) {
      return processSolanaBulkSwapAction(ctx, req)
    }
    return Result.fail(UnknownError.fromUnknown('not implemented'))
  },
  'processBulkSwapAction',
)

export const processSwapAction = action(async (ctx, req: ProcessSwapArgs) => {
  if (req.network === Network.Solana) {
    return processSolanaSwapAction(ctx, req)
  }

  const { secureStorageService } = ctx.get(dependenciesAtom)
  const inviteCode = ctx.get(existingInviteCodeAtom)
  let permit:
    | {
        permitSingle: TradePreparePermitInstructionsCreateData['permitSingle']
        signedPermit: string
      }
    | undefined = undefined

  if (Network.isEvmNetwork(req.network)) {
    const signedPermitResult = await ctx.schedule(() =>
      prepareAndSignPermitAction(ctx, {
        deadline: req.swapSettings.deadline,
        amount: req.amount.toString(),
        network: Network.toDto(req.network),
        provider: req.provider.id,
        walletId: req.walletId,
        tokenAddress: req.pairQuoteCurrency,
        walletAddress: req.walletAddress,
      }),
    )

    if (Result.isFailure(signedPermitResult)) {
      return Result.fail(signedPermitResult.failure)
    }

    permit = {
      permitSingle: signedPermitResult.success.permitSingle,
      signedPermit: signedPermitResult.success.signedPermit,
    }
  }

  const feeConfig = req.quote.feeConfig
  const blockhash = ctx.get(blockHashAtom)?.blockhash
  const accountsDto = req.accounts?.[req.type]
  const accounts = accountsDto
    ? Pair.poolAccountsToSecureStoragePoolAccounts(req.provider, accountsDto)
    : undefined
  if (!feeConfig || !blockhash || !accounts) {
    // eslint-disable-next-line no-console
    console.error(
      `Missing ${[
        ['feeConfig', feeConfig],
        ['blockhash', blockhash],
        ['accounts', accounts],
      ]
        .filter(([_, value]) => value === undefined)
        .map(([key]) => key)}`,
    )
    return Result.fail(UnknownError.fromUnknown('Invalid input'))
  }

  const amountIn = BigNumber(req.quote.inputAmountRaw).integerValue(
    BigNumber.ROUND_FLOOR,
  )
  const amountOut = BigNumber(req.quote.outputAmountRaw)
    .multipliedBy(1 - req.swapSettings.slippage / 100)
    .integerValue(BigNumber.ROUND_FLOOR)

  const signedResult = await ctx.schedule(() => {
    return secureStorageService.buildAndSignSwapTransaction({
      walletId: req.walletId,
      operation: req.type,
      provider: req.provider.id,
      referralCode: inviteCode,
      mint: req.pairQuoteCurrency,
      tokenProgram: req.pairQuoteTokenProgram,
      amountIn: amountIn.toString(),
      amountOut: amountOut.toString(),
      signer: req.walletAddress,
      poolAccounts: accounts,
      feeConfig,
      blockhash,
      poolId: req.pairAddress,
      useHeliusSender: false,
      jitoEnabled: undefined,
      jitoTipSol: undefined,
      priorityFeeSol: undefined,
    })
  })

  if (Result.isFailure(signedResult)) {
    const error = Error.UnknownError.fromUnknown(signedResult.failure)
    return Result.fail(error)
  }

  const sendResult = await ctx.schedule(() =>
    sendSwapTransactionRequestAction(ctx, {
      ...req,
      signedTx: signedResult.success.txSigned,
      permit: permit,
    }),
  )

  if (Result.isSuccess(sendResult)) {
    swapDoneEvent(ctx, {
      pairAddress: req.pairAddress,
      txHash: sendResult.success.tx,
    })
  }

  return sendResult
}, 'processSwapAction')

export const showSwapErrorSnackbarAction = action(
  (
    ctx,
    currency: CurrencyModel | undefined,
    error: Error.UnknownError | ApiError.ApiError,
  ) => {
    if (ApiError.isOfCode(SwapErrorCode.InsufficientBalance)(error)) {
      showSnackbarAction(ctx, {
        text: i18next.t('swap.error'),
        description: i18next.t('trading.error.insufficient-funds', {
          ticker: currency?.ticker ?? '',
        }),
        intent: 'error',
      })
      return
    }

    showSnackbarAction(ctx, {
      text: i18next.t('swap.error'),
      description: i18next.t('common.something-went-wrong'),
      intent: 'error',
    })
  },
  'showSwapErrorSnackbarAction',
)

export const reatomSwapRequest = () => {
  const {
    executeAction: executeSwapAction,
    requestStateAtom: swapRequestState,
    resetStateAction: resetAction,
  } = reatomRequest({
    execAction: processSwapAction,
  })

  return swapRequestState.pipe(
    withAssign(() => ({
      execute: executeSwapAction,
      reset: resetAction,
    })),
  )
}

// eslint-disable-next-line @reatom/unit-naming-rule
export const swapDoneEvent = action<{
  pairAddress: PairAddress
  txHash: TxHash
}>('swapDoneEvent')
